import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { FullPageSpinner } from '@Components/Spinner';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import {
  CpsApprovalStatus,
  CpsManagedEmployeeResponse,
  CpsReportDetailsResponse,
  ReportSubTab
} from './cps-utils';
import { ReportListItem } from './ReportListItem';
import { ReportReview } from './ReportReview';
import { awaitJSON, ClientToolsLambdaFetch } from '../../utils/fetch-utils';
import { useSetError } from '../../redux/modals';

interface ReportsTabProps {
  cpsUserName: string;
}

const ReportsTab = ({ cpsUserName: _cpsUserName }: ReportsTabProps) => {
  const setError = useSetError();
  const [employees, setEmployees] = useState<CpsManagedEmployeeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeIndirects, setIncludeIndirects] = useState(false);
  const [subTab, setSubTab] = useState<string>(ReportSubTab.PENDING);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  const fetchEmployees = useCallback(
    async (indirects: boolean) => {
      try {
        setLoading(true);
        const res = await ClientToolsLambdaFetch('/cps/managedEmployees', {
          params: { includeIndirects: indirects }
        });
        const data = await awaitJSON<CpsManagedEmployeeResponse[]>(res);
        setEmployees(data);
      } catch (e) {
        setError({ message: `Failed to load managed employees: ${e.message}`, reportError: e });
      } finally {
        setLoading(false);
      }
    },
    [setError]
  );

  useEffect(() => {
    fetchEmployees(includeIndirects);
  }, [includeIndirects, fetchEmployees]);

  const reportsByStatus = useMemo(() => {
    const reports: CpsReportDetailsResponse[] = employees
      .filter(emp => emp.lastReport)
      .map(emp => emp.lastReport!);

    const pending = reports.filter(r => r.approvalStatus === String(CpsApprovalStatus.PENDING));
    const rejected = reports.filter(r => r.approvalStatus === String(CpsApprovalStatus.REJECTED));
    const approved = reports.filter(r => r.approvalStatus === String(CpsApprovalStatus.APPROVED));
    const awaiting = employees.filter(
      emp => !emp.lastReport || emp.lastReport.approvalStatus === String(CpsApprovalStatus.DRAFT)
    );

    return { pending, rejected, approved, awaiting };
  }, [employees]);

  const handleBack = useCallback(() => {
    setSelectedReportId(null);
    fetchEmployees(includeIndirects);
  }, [includeIndirects, fetchEmployees]);

  if (selectedReportId) {
    return <ReportReview reportId={selectedReportId} onBack={handleBack} />;
  }

  if (loading) return <FullPageSpinner />;

  const getReportsForSubTab = (): CpsReportDetailsResponse[] => {
    if (subTab === ReportSubTab.PENDING) return reportsByStatus.pending;
    if (subTab === ReportSubTab.REJECTED) return reportsByStatus.rejected;
    if (subTab === ReportSubTab.APPROVED) return reportsByStatus.approved;
    return [];
  };
  const currentReports = getReportsForSubTab();

  return (
    <div className='cps-reports-tab'>
      <div className='cps-reports-controls'>
        <ToggleSwitch
          data-testid='cps-include-indirects-toggle'
          label='Include indirect reports'
          checked={includeIndirects}
          onChange={setIncludeIndirects}
        />
      </div>

      <Nav
        variant='pills'
        activeKey={subTab}
        onSelect={key => key && setSubTab(key)}
        className='cps-reports-sub-tabs'
      >
        <Nav.Item>
          <Nav.Link eventKey={ReportSubTab.PENDING}>
            Pending ({reportsByStatus.pending.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey={ReportSubTab.REJECTED}>
            Rejected ({reportsByStatus.rejected.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey={ReportSubTab.APPROVED}>
            Approved ({reportsByStatus.approved.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey={ReportSubTab.AWAITING}>
            Awaiting ({reportsByStatus.awaiting.length})
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className='cps-reports-list'>
        <ReportsList
          subTab={subTab}
          awaiting={reportsByStatus.awaiting}
          currentReports={currentReports}
          onView={setSelectedReportId}
        />
      </div>
    </div>
  );
};

export { ReportsTab };

interface ReportsListProps {
  subTab: string;
  awaiting: CpsManagedEmployeeResponse[];
  currentReports: CpsReportDetailsResponse[];
  onView: Dispatch<SetStateAction<string | null>>;
}

const ReportsList = ({ subTab, awaiting, currentReports, onView }: ReportsListProps) => {
  if (subTab === ReportSubTab.AWAITING) {
    if (awaiting.length === 0) {
      return <p className='cps-reports-empty'>All employees have submitted.</p>;
    }
    return (
      <>
        {awaiting.map(emp => (
          <div key={emp.id} className='cps-report-list-item'>
            <div className='cps-report-list-item-info'>
              <span className='cps-report-list-item-name'>{emp.name}</span>
              <span className='cps-report-list-item-period'>Awaiting submission</span>
            </div>
          </div>
        ))}
      </>
    );
  }

  if (currentReports.length === 0) {
    return <p className='cps-reports-empty'>No reports in this category.</p>;
  }

  return (
    <>
      {currentReports.map(report => (
        <ReportListItem key={report.id} report={report} onView={onView} />
      ))}
    </>
  );
};
